import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import GoalRushStats from './GoalRushStats';
import RefreshButton from './RefreshButton';

const fmtDay = (day) => new Date(`${day}T00:00:00`).toLocaleDateString();

/**
 * GoalRush analytics (brief §7) as a right-hand drawer over Insights, in the same shell as
 * ItemActivityDrawer. The parent owns the fetch, so the drawer follows the page's team and date
 * filters while it is open.
 */
const GoalRushAnalyticsDrawer = ({
  data,
  loading,
  refreshing,
  team,
  fromDate,
  toDate,
  onRefresh,
  onClose
}) => {
  const { t } = useTranslation();
  const k = (key, opts) => t(`admin.insights.goalrushStats.${key}`, opts);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const teamLabel = team ? team.name || team.username : k('scopeAllTeams');
  const rangeLabel =
    fromDate || toDate
      ? `${fromDate ? fmtDay(fromDate) : '…'} – ${toDate ? fmtDay(toDate) : '…'}`
      : k('scopeAllTime');

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      <div className="relative w-full max-w-3xl bg-white h-full shadow-xl flex flex-col">
        <div className="flex items-start gap-3 px-5 py-4 border-b border-[#E2E8F0]">
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-semibold text-[#0F172A] truncate">{k('title')}</p>
            <p className="text-[12px] text-[#64748B] truncate">
              {teamLabel} · {rangeLabel}
            </p>
            <p className="text-[12px] text-[#94A3B8]">{k('subtitle')}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <RefreshButton
              onClick={onRefresh}
              refreshing={refreshing}
              disabled={loading}
              title={k('refresh')}
            />
            <button
              onClick={onClose}
              className="text-[12px] font-medium text-[#64748B] hover:text-[#334155] transition-colors"
            >
              {t('admin.insights.drawer.close')}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pt-2 pb-6">
          <GoalRushStats data={data} loading={loading} hideHeader stacked />
        </div>
      </div>
    </div>
  );
};

export default GoalRushAnalyticsDrawer;
